import { NotaryRole, useNotariesQuery } from 'generated/gql';
import { t } from '@lingui/macro';
import { InputField } from 'components/FormFields/InputField';
import { getDeedExecutorLabel } from 'helpers/formatName';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Flex, Link, MinusIcon, PlusIcon, Select } from '@rulebooks/evidend-titan';
import type { SelectOptionProps } from '@rulebooks/evidend-titan';
import type { MenuItemOptionProps } from '@rulebooks/evidend-titan/dist/types/components/Menu/MenuItemOption';
import styled from 'styled-components';
import * as yup from 'yup';
import { SelectField } from '../../components/FormFields/SelectField';
import { integerTransform } from '../helpers/integerTransform';

type NotaryOptionProps = Pick<
  MenuItemOptionProps,
  | 'isDisabled'
  | 'hasEllipsis'
  | 'description'
  | 'descriptionLineClamp'
  | 'metadata'
  | 'variant'
  | 'prefixAdornment'
  | 'formatters'
> & {
  value: string;
  label: string;
};

const NotaryOption = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NotaryOptionLine = styled.div.withConfig<{ field: 'name' | 'location' | 'group' }>({
  shouldForwardProp: (prop, defaultValidationFn) => defaultValidationFn(prop),
})`
  color: ${(props) => {
    switch (props.field) {
      case 'name':
        return props.theme.designTokens.colors.grey90;
      case 'location':
        return props.theme.designTokens.colors.grey70;
      case 'group':
        return props.theme.designTokens.colors.grey50;
    }
  }};
`;

const getNotaryNameSchema = () =>
  yup.object({
    initials: yup.string().trim().required(),
    preposition: yup.string().trim().nullable(),
    lastName: yup.string().trim().required(),
  });
const notaryNameSchema = getNotaryNameSchema();

const getDocumentDeedPassedFormSchema = () => {
  return yup.object({
    notary: yup.mixed().when('notaryRole', {
      is: (val: NotaryRole) => val !== NotaryRole.UnknownNotary,
      then: yup.object({
        name: notaryNameSchema,
        location: yup.string().trim().required(),
      }),
      otherwise: yup.mixed().nullable().optional(),
    }),
    notaryRole: yup.mixed().oneOf(Object.values(NotaryRole)).required(),
    assignedNotary: yup.mixed().when('notaryRole', {
      is: (val: NotaryRole) => val === NotaryRole.AssignedNotary,
      then: yup.object({
        name: notaryNameSchema,
        location: yup.string().trim().required(),
      }),
      otherwise: yup.mixed().nullable().optional(),
    }),
  });
};

export const documentDeedPassedFormSchema = getDocumentDeedPassedFormSchema();

export type DocumentDeedPassedFormValues = yup.InferType<typeof documentDeedPassedFormSchema>;

interface IDocumentDeedPassedForm {
  nestedField?: string;
}

export const notaryRoleOptions = () => [
  {
    label: t`Notary`,
    value: NotaryRole.Notary,
  },
  {
    label: t`Deputy notary`,
    value: NotaryRole.DeputyNotary,
  },
  {
    label: t`Assigned civil-law notary`,
    value: NotaryRole.AssignedNotary,
  },
  {
    label: t`Notary unknown`,
    value: NotaryRole.UnknownNotary,
  },
];

const DocumentDeedPassedForm = ({ nestedField }: IDocumentDeedPassedForm = { nestedField: '' }) => {
  const { watch, setValue, getValues, trigger } = useFormContext();
  const notaryRole = watch(`${nestedField}notaryRole`);
  const [selectedNotaryId, setSelectedNotaryId] = useState<string | null>(null);
  const [selectedAssignedNotaryId, setSelectedAssignedNotaryId] = useState<string | null>(null);
  const [isManualEntry, setIsManualEntry] = useState<boolean>(false);
  const [isManualCivilLawNotary, setIsManuaCivilLawNotary] = useState<boolean>(false);
  const hasInitializedRef = useRef<boolean>(false);
  const { data: { notaries } = {} } = useNotariesQuery();

  useEffect(() => {
    if (!notaryRole) {
      setValue(`${nestedField}notaryRole`, NotaryRole.Notary, {
        shouldValidate: true,
        shouldDirty: false,
      });
      setValue(`${nestedField}notary`, null);
    }
  }, []);

  useEffect(() => {
    if (notaryRole === NotaryRole.UnknownNotary) {
      setValue(`${nestedField}notary`, null);
      setSelectedNotaryId(null);
    }
  }, [nestedField, notaryRole, setValue]);

  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (!notaries) return;

    const findMatch = (
      data: {
        name?: {
          initials?: string | null;
          preposition?: string | null;
          lastName?: string | null;
        } | null;
        location?: string | null;
      } | null,
    ) => {
      if (!data?.name?.lastName) return null;
      const initials = (data.name.initials ?? '').trim();
      const preposition = (data.name.preposition ?? '').trim();
      const lastName = data.name.lastName.trim();
      const location = (data.location ?? '').trim();
      return (
        notaries.find(
          (n) =>
            n.active &&
            n.initials.trim() === initials &&
            (n.prefix ?? '').trim() === preposition &&
            n.lastName.trim() === lastName &&
            n.location.trim() === location,
        ) ?? null
      );
    };

    const existingNotary = getValues(`${nestedField}notary`);
    if (existingNotary) {
      const match = findMatch(existingNotary);
      if (match) {
        setSelectedNotaryId(match.id);
      } else {
        setIsManualEntry(true);
      }
    }

    const existingAssignedNotary = getValues(`${nestedField}assignedNotary`);
    if (existingAssignedNotary) {
      const match = findMatch(existingAssignedNotary);
      if (match) {
        setSelectedAssignedNotaryId(match.id);
      } else {
        setIsManuaCivilLawNotary(true);
      }
    }

    hasInitializedRef.current = true;
  }, [notaries, nestedField, getValues]);

  const options = notaryRoleOptions();

  const notaryOptions = useMemo<NotaryOptionProps[]>(
    () =>
      (notaries ?? [])
        .filter((n) => n.active)
        .map((n) => {
          const fullName = [n.initials, n.prefix, n.lastName].filter(Boolean).join(' ');
          return {
            value: n.id,
            label: `${fullName} — ${n.location}`,
            formatters: {
              label: () => (
                <NotaryOption>
                  <NotaryOptionLine field="name">{fullName}</NotaryOptionLine>
                  <NotaryOptionLine field="location">{n.location}</NotaryOptionLine>
                  {n.group?.name && (
                    <NotaryOptionLine field="group">{n.group.name}</NotaryOptionLine>
                  )}
                </NotaryOption>
              ),
            },
          };
        }),
    [notaries],
  );

  const handleExecutedBy = (notaryId: string) => {
    setIsManualEntry(false);
    if (!notaryId) {
      setSelectedNotaryId(null);
      setValue(`${nestedField}notary`, null, { shouldDirty: true });
      void trigger();
      return;
    }
    setSelectedNotaryId(notaryId);
    const notary = notaries?.find((n) => n.id === notaryId);
    if (!notary) return;
    setValue(`${nestedField}notary.name.initials`, notary.initials, { shouldDirty: true });
    setValue(`${nestedField}notary.name.preposition`, notary.prefix ?? '', { shouldDirty: true });
    setValue(`${nestedField}notary.name.lastName`, notary.lastName, { shouldDirty: true });
    setValue(`${nestedField}notary.location`, notary.location, { shouldDirty: true });
    trigger();
  };

  const handleExecutedByCivilLawNotary = (notaryId: string) => {
    setIsManuaCivilLawNotary(false);
    if (!notaryId) {
      setSelectedAssignedNotaryId(null);
      setValue(`${nestedField}assignedNotary`, null, { shouldDirty: true });
      trigger();
      return;
    }
    setSelectedAssignedNotaryId(notaryId);
    const notary = notaries?.find((n) => n.id === notaryId);
    if (!notary) return;
    setValue(`${nestedField}assignedNotary.name.initials`, notary.initials, { shouldDirty: true });
    setValue(`${nestedField}assignedNotary.name.preposition`, notary.prefix ?? '', {
      shouldDirty: true,
    });
    setValue(`${nestedField}assignedNotary.name.lastName`, notary.lastName, {
      shouldDirty: true,
    });
    setValue(`${nestedField}assignedNotary.location`, notary.location, { shouldDirty: true });
    trigger();
  };

  const handleNotaryRoleChange = (role: string) => {
    const newRole = role as NotaryRole;
    if (newRole === NotaryRole.UnknownNotary) {
      setIsManualEntry(false);
    }
    if (newRole !== NotaryRole.AssignedNotary) {
      setValue(`${nestedField}assignedNotary`, null, { shouldDirty: true });
      setSelectedAssignedNotaryId(null);
      setIsManuaCivilLawNotary(false);
    }
    setValue(`${nestedField}notaryRole`, newRole, {
      shouldValidate: true,
      shouldDirty: false,
    });
  };

  const handleEnterManually = (isManual: boolean) => {
    setIsManualEntry(isManual);
  };

  const handleIsManualCivilLaw = (isManual: boolean) => {
    setIsManuaCivilLawNotary(isManual);
  };

  const showAssignedNotaryFields = notaryRole === NotaryRole.AssignedNotary;

  return (
    <>
      <SelectField
        required
        label={t`Gepasseerd door`}
        name={`${nestedField}notaryRole`}
        options={options}
        placeholder={t`Gepasseerd door`}
        transform={integerTransform}
        onChange={handleNotaryRoleChange}
        value={notaryRole}
      />
      {showAssignedNotaryFields && (
        <>
          <Select
            name={`${nestedField}assignedNotarySearch`}
            label={t`Assigned civil-law notary`}
            placeholder={t`Select Notary`}
            options={notaryOptions as SelectOptionProps[]}
            isSearchable
            isClearable
            value={selectedAssignedNotaryId ?? ''}
            onChange={handleExecutedByCivilLawNotary as never}
            spaceAfter={6}
            required={true}
            labelNoOptions={() => (
              <Flex alignItems="center" flexDirection="column" gap={2}>
                {t`No results found`}
              </Flex>
            )}
          />

          {isManualCivilLawNotary ? (
            <Link
              prefixAdornment={<MinusIcon />}
              onClick={() => handleIsManualCivilLaw(false)}
              spaceAfter={6}
            >
              {t`Hide details`}
            </Link>
          ) : (
            <Link
              prefixAdornment={<PlusIcon />}
              onClick={() => handleIsManualCivilLaw(true)}
              spaceAfter={6}
            >
              {t`Enter details manually`}
            </Link>
          )}

          {isManualCivilLawNotary && (
            <NotaryForm
              labelNotary={t`Toegevoegd notaris`}
              labelNotaryLocation={t`Vestigingsplaats toegevoegd notaris`}
              nestedField={`${nestedField}assignedNotary.`}
            />
          )}
        </>
      )}

      {notaryRole !== NotaryRole.UnknownNotary && (
        <>
          <Select
            name={`${nestedField}notarySearch`}
            label={getDeedExecutorLabel(notaryRole)}
            placeholder={t`Select Notary`}
            options={notaryOptions as SelectOptionProps[]}
            isSearchable
            isClearable
            value={selectedNotaryId ?? ''}
            onChange={handleExecutedBy as never}
            spaceAfter={6}
            required={true}
            labelNoOptions={() => (
              <Flex alignItems="center" flexDirection="column" gap={2}>
                {t`No results found`}
              </Flex>
            )}
          />

          {isManualEntry ? (
            <Link
              prefixAdornment={<MinusIcon />}
              onClick={() => handleEnterManually(false)}
              spaceAfter={6}
            >
              {t`Hide details`}
            </Link>
          ) : (
            <Link
              prefixAdornment={<PlusIcon />}
              onClick={() => handleEnterManually(true)}
              spaceAfter={6}
            >
              {t`Enter details manually`}
            </Link>
          )}

          {isManualEntry && <NotaryForm nestedField={`${nestedField}notary.`} />}
        </>
      )}
    </>
  );
};

interface INotaryForm {
  nestedField?: string;
  labelNotary?: string;
  labelNotaryLocation?: string;
}
export const NotaryForm = ({
  nestedField,
  labelNotary = t`Notaris`,
  labelNotaryLocation = t`Vestigingsplaats notaris`,
}: INotaryForm) => {
  return (
    <>
      <InputField
        required
        label={labelNotary}
        name={`${nestedField}name.initials`}
        placeholder={t`Initialen`}
        spaceAfter={2}
      />
      <InputField
        isLabelHidden
        label={t`Tussenvoegsel`}
        name={`${nestedField}name.preposition`}
        placeholder={t`Tussenvoegsel`}
        spaceAfter={2}
      />
      <InputField
        isLabelHidden
        required
        label={t`Achternaam`}
        name={`${nestedField}name.lastName`}
        placeholder={t`Achternaam`}
        spaceAfter={6}
      />

      <InputField
        required
        label={labelNotaryLocation}
        name={`${nestedField}location`}
        placeholder={labelNotaryLocation}
        spaceAfter={6}
      />
    </>
  );
};

export { DocumentDeedPassedForm, documentDeedPassedFormSchema as DocumentDeedPassedFormSchema };
